import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { X, MessageCircle, CheckCheck, Loader2, Mic, ChevronDown, ChevronUp, AlertCircle, Video } from 'lucide-react';
import { ParsedWhatsAppMessage, parseWhatsAppThread } from '../../utils/whatsappThreadParser';
import { useTimezone } from '../../hooks/useTimezone';
import { supabase } from '../../lib/supabase';
import { toLocaleTimeStringInTz, toLocaleDateStringInTz } from '../../utils/dateFormatters';

interface WhatsAppThreadModalProps {
  isOpen: boolean;
  onClose: () => void;
  leadId?: string;
  threadText?: string;
  leadName?: string;
  companyName?: string;
}

function formatMsgTime(timestamp: string, tz?: string): string {
  try {
    const date = new Date(timestamp);
    return toLocaleTimeStringInTz(date, tz, 'en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  } catch {
    return '';
  }
}

function getMsgDateGroup(timestamp: string, tz?: string): string {
  try {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInDays === 0) return 'Today';
    if (diffInDays === 1) return 'Yesterday';

    return toLocaleDateStringInTz(date, tz, 'en-US', {
      weekday: 'long',
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    });
  } catch {
    return '';
  }
}

const WhatsAppThreadModal: React.FC<WhatsAppThreadModalProps> = ({
  isOpen,
  onClose,
  leadId,
  threadText,
  leadName = 'Lead',
  companyName,
}) => {
  const tz = useTimezone();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [messages, setMessages] = useState<ParsedWhatsAppMessage[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setMessages([]);
      return;
    }

    if (leadId) {
      let cancelled = false;
      setLoading(true);

      supabase
        .from('whatsapp_logs')
        .select('from_role, message_text, message_type, status, created_at, audio_url, video_url')
        .eq('lead_id', leadId)
        .order('created_at', { ascending: true })
        .then(({ data, error }) => {
          if (cancelled) return;
          if (!error && data && data.length > 0) {
            setMessages(data.map((msg) => {
              const sender: 'user' | 'assistant' = msg.from_role === 'lead' ? 'user' : 'assistant';
              const ts = msg.created_at || '';
              return {
                timestamp: ts,
                formattedDate: formatMsgTime(ts, tz),
                sender,
                body: msg.message_text || '(empty)',
                dateGroup: getMsgDateGroup(ts, tz),
                messageType: msg.message_type || undefined,
                audioUrl: msg.audio_url || null,
                videoUrl: msg.video_url || null,
                deliveryStatus: msg.status || null,
              };
            }));
          } else if (threadText) {
            setMessages(parseWhatsAppThread(threadText, tz));
          } else {
            setMessages([]);
          }
          setLoading(false);
        });

      return () => { cancelled = true; };
    } else if (threadText) {
      setMessages(parseWhatsAppThread(threadText, tz));
    } else {
      setMessages([]);
    }
  }, [isOpen, leadId]);

  useEffect(() => {
    if (isOpen && scrollRef.current && !loading && messages.length > 0) {
      setTimeout(() => {
        scrollRef.current?.scrollTo({ top: scrollRef.current!.scrollHeight });
      }, 50);
    }
  }, [isOpen, loading, messages]);

  if (!isOpen) return null;

  const subtitle = companyName
    ? `Conversation with ${leadName} from ${companyName}`
    : `Conversation with ${leadName}`;

  let lastDateGroup = '';

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col shadow-2xl shadow-black/20 animate-fadeIn">
        <div className="flex items-center justify-between px-6 py-5 border-b border-[#05E0D1]/20 bg-gradient-to-r from-[#1875FF]/5 to-[#05E0D1]/5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-[#1875FF] to-[#05E0D1] rounded-xl flex items-center justify-center shadow-lg">
              <MessageCircle className="text-white" size={18} />
            </div>
            <div>
              <h3 className="text-lg font-extrabold text-gray-900 font-display">WhatsApp Thread</h3>
              <p className="text-xs text-gray-500 font-medium">{subtitle}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-white rounded-lg"
          >
            <X size={20} />
          </button>
        </div>

        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto custom-scrollbar bg-white"
        >
          {loading ? (
            <div className="text-center py-16">
              <Loader2 size={32} className="mx-auto text-[#05E0D1] mb-3 animate-spin" />
              <p className="text-gray-400 text-sm font-medium">Loading messages...</p>
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center py-16">
              <MessageCircle size={40} className="mx-auto text-gray-300 mb-3" />
              <p className="text-gray-400 text-sm font-medium">No messages to display</p>
            </div>
          ) : (
            <div className="py-5 px-5 space-y-2.5">
              {messages.map((message, index) => {
                const showDateSeparator =
                  message.dateGroup && message.dateGroup !== lastDateGroup;
                if (message.dateGroup) lastDateGroup = message.dateGroup;

                return (
                  <React.Fragment key={index}>
                    {showDateSeparator && (
                      <WhatsAppDateSeparator label={message.dateGroup} />
                    )}
                    <WhatsAppBubble message={message} />
                  </React.Fragment>
                );
              })}
            </div>
          )}
        </div>

        <div className="px-6 py-3 border-t border-gray-100 bg-white">
          <p className="text-xs text-gray-400 text-center font-medium">
            {messages.length} message{messages.length !== 1 ? 's' : ''} in this conversation
          </p>
        </div>
      </div>
    </div>,
    document.body
  );
};

const WhatsAppDateSeparator: React.FC<{ label: string }> = ({ label }) => (
  <div className="flex items-center justify-center py-3">
    <div className="flex items-center gap-3 w-full">
      <div className="flex-1 h-px bg-gray-200" />
      <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest font-sans px-2">
        {label}
      </span>
      <div className="flex-1 h-px bg-gray-200" />
    </div>
  </div>
);

interface WhatsAppBubbleProps {
  message: ParsedWhatsAppMessage;
}

const WhatsAppBubble: React.FC<WhatsAppBubbleProps> = ({ message }) => {
  const isAgent = message.sender === 'assistant';
  const isAudio = message.messageType === 'audio' && message.audioUrl;
  const isVideo = message.messageType === 'video' && message.videoUrl;
  const [showTranscript, setShowTranscript] = useState(false);
  const [videoError, setVideoError] = useState(false);

  return (
    <div className={`flex ${isAgent ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`relative max-w-[75%] px-3.5 py-2.5 ${
          isAgent
            ? 'bg-[#05E0D1]/15 rounded-lg rounded-tr-sm border border-[#05E0D1]/20'
            : 'bg-gray-200 rounded-lg rounded-tl-sm'
        }`}
      >
        {isVideo ? (
          <div className="min-w-[280px]">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-5 h-5 bg-gradient-to-br from-[#05E0D1] to-[#1875FF] rounded-md flex items-center justify-center">
                <Video className="text-white" size={10} />
              </div>
              <span className="text-[11px] font-semibold text-gray-600">Video Message</span>
            </div>
            {videoError ? (
              <div className="w-full rounded-md bg-gray-100 border border-gray-200 flex flex-col items-center justify-center py-6 px-4">
                <AlertCircle size={24} className="text-gray-400 mb-2" />
                <span className="text-xs text-gray-500 font-medium">Video unavailable</span>
              </div>
            ) : (
              <video
                controls
                className="w-full rounded-md"
                preload="metadata"
                style={{ maxHeight: '240px' }}
                onError={() => setVideoError(true)}
              >
                <source src={message.videoUrl!} type="video/mp4" />
              </video>
            )}
            {message.body && message.body !== '(empty)' && (
              <div className="mt-2">
                <button
                  onClick={() => setShowTranscript(!showTranscript)}
                  className="flex items-center gap-1 text-[11px] font-medium text-[#1875FF] hover:text-[#1260CC] transition-colors"
                >
                  {showTranscript ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                  Video transcript
                </button>
                {showTranscript && (
                  <p className="mt-1.5 text-[12px] leading-relaxed text-gray-500 border-t border-gray-200/60 pt-1.5 whitespace-pre-wrap">
                    {message.body}
                  </p>
                )}
              </div>
            )}
          </div>
        ) : isAudio ? (
          <div className="min-w-[240px]">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-5 h-5 bg-gradient-to-br from-[#05E0D1] to-[#1875FF] rounded-md flex items-center justify-center">
                <Mic className="text-white" size={10} />
              </div>
              <span className="text-[11px] font-semibold text-gray-600">Voice Note</span>
            </div>
            <audio
              controls
              className="w-full h-8"
              preload="metadata"
              style={{ minWidth: '220px' }}
            >
              <source src={message.audioUrl!} type="audio/ogg" />
              <source src={message.audioUrl!} type="audio/mpeg" />
              <source src={message.audioUrl!} type="audio/mp4" />
            </audio>
            {message.body && message.body !== '(empty)' && (
              <div className="mt-2">
                <button
                  onClick={() => setShowTranscript(!showTranscript)}
                  className="flex items-center gap-1 text-[11px] font-medium text-[#1875FF] hover:text-[#1260CC] transition-colors"
                >
                  {showTranscript ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                  Voice note transcript
                </button>
                {showTranscript && (
                  <p className="mt-1.5 text-[12px] leading-relaxed text-gray-500 border-t border-gray-200/60 pt-1.5 whitespace-pre-wrap">
                    {message.body}
                  </p>
                )}
              </div>
            )}
          </div>
        ) : (
          <p className={`text-[13.5px] leading-relaxed whitespace-pre-wrap break-words pr-16 ${
            isAgent ? 'text-gray-800' : 'text-gray-700'
          }`}>
            {message.body}
          </p>
        )}

        {isAgent && message.deliveryStatus === 'failed' ? (
          <div className="flex items-center gap-1 justify-end mt-1">
            <span className="text-[10.5px] text-red-400">Not delivered</span>
            <span className="text-[10.5px] text-red-300">|</span>
            <span className="text-[10.5px] text-red-400">{message.formattedDate}</span>
            <AlertCircle size={12} className="text-red-400" />
          </div>
        ) : (
          <div className={`flex items-center gap-1 justify-end ${isAudio || isVideo ? 'mt-1' : '-mt-3'} float-right ml-3 relative top-1`}>
            {message.formattedDate && (
              <span className={`text-[10.5px] ${isAgent ? 'text-[#05E0D1]/80' : 'text-gray-400'}`}>
                {message.formattedDate}
              </span>
            )}
            {isAgent && <CheckCheck size={14} className="text-[#05E0D1]" />}
          </div>
        )}
      </div>
    </div>
  );
};

export default WhatsAppThreadModal;
